import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { PERMISSIONS, USER_TYPES } from '@/constants/permissions'

export interface MenuItem {
  title: string
  subtitle: string
  icon: string
  to: string
  roles?: string[]           // Rôles autorisés
  permissions?: string[]     // Permissions requises
  userTypes?: string[]       // Types d'utilisateur autorisés
  hidden?: boolean           // Masquer complètement
}

export function useMenuPermissions() {
  const authStore = useAuthStore()

  // Vérifier si un élément de menu est visible
  const isMenuItemVisible = (item: MenuItem): boolean => {
    // Si l'élément est explicitement masqué
    if (item.hidden) {
      return false
    }

    const user = authStore.user
    if (!user) {
      return false
    }

    // Normaliser le userType pour la comparaison (gérer les cas majuscule/minuscule)
    const normalizedUserType = user.userType?.toLowerCase() || user.userType

    // Si aucun contrôle d'accès n'est défini, afficher par défaut
    if (!item.roles && !item.permissions && !item.userTypes) {
      return true
    }

    // Vérifier les rôles
    if (item.roles && item.roles.length > 0) {
      if (!item.roles.includes(user.role)) {
        return false
      }
    }

    // Vérifier les permissions
    if (item.permissions && item.permissions.length > 0) {
      const hasRequiredPermission = item.permissions.some(permission => 
        authStore.hasPermission(permission)
      )
      if (!hasRequiredPermission) {
        return false
      }
    }

    // Vérifier les types d'utilisateur (normaliser pour la comparaison)
    if (item.userTypes && item.userTypes.length > 0) {
      const normalizedUserTypes = item.userTypes.map(ut => ut?.toLowerCase() || ut)
      if (!normalizedUserTypes.includes(normalizedUserType)) {
        return false
      }
    }

    return true
  }

  // Filtrer les éléments de menu visibles
  const getVisibleMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter(isMenuItemVisible)
  }

  // Vérifier si l'utilisateur a accès à une route spécifique
  const canAccessRoute = (route: string): boolean => {
    const menuItem = allMenuItems.find(item => item.to === route)
    return menuItem ? isMenuItemVisible(menuItem) : true
  }

  // Définition complète des éléments de menu avec permissions
  const allMenuItems: MenuItem[] = [
    {
      title: 'Vue d\'ensemble (Dashboards)',
      subtitle: 'Synthèse KPI & widgets',
      icon: 'mdi-view-dashboard',
      to: '/dashboard',
      // Accessible à tous les utilisateurs connectés
    },
    {
      title: 'Éditeur de tableaux de bord',
      subtitle: 'Construire et publier',
      icon: 'mdi-chart-box',
      to: '/dashboards',
      permissions: [PERMISSIONS.DASHBOARD.VIEW, PERMISSIONS.DASHBOARD.CONFIGURE]
    },
    {
      title: 'Employés',
      subtitle: 'Gestion du personnel',
      icon: 'mdi-id-card',
      to: '/employes',
      permissions: [PERMISSIONS.EMPLOYEE.READ]
    },
    {
      title: 'Paie',
      subtitle: 'Gestion des salaires',
      icon: 'mdi-calculator',
      to: '/paie',
      permissions: [PERMISSIONS.PAYROLL.READ]
    },
    {
      title: 'Absences',
      subtitle: 'Gestion des congés',
      icon: 'mdi-calendar-remove',
      to: '/absences',
      // Accessible à tous sauf les viewers externes
      userTypes: [USER_TYPES.INTERNAL, USER_TYPES.EXTERNAL],
      permissions: [PERMISSIONS.ABSENCE.READ]
    },
    {
      title: 'Présence',
      subtitle: 'Suivi des activités',
      icon: 'mdi-clock-check',
      to: '/presence',
      permissions: [PERMISSIONS.PRESENCE.READ]
    },
    {
      title: 'Rémunération',
      subtitle: 'Salaires et primes',
      icon: 'mdi-wallet',
      to: '/remuneration',
      permissions: [PERMISSIONS.PAYROLL.READ, PERMISSIONS.PAYROLL.MANAGE_SALARIES]
    },
    {
      title: 'Notes de frais',
      subtitle: 'Gestion des dépenses',
      icon: 'mdi-receipt',
      to: '/notes-frais',
      // Accessible à tous les utilisateurs internes
      userTypes: [USER_TYPES.INTERNAL, USER_TYPES.EXTERNAL],
      permissions: [PERMISSIONS.EXPENSE.READ]
    },
    {
      title: 'Syndicats',
      subtitle: 'Gestion syndicale',
      icon: 'mdi-handshake',
      to: '/syndicats',
      permissions: [PERMISSIONS.UNION.READ, PERMISSIONS.UNION.UPDATE]
    },
    {
      title: 'Équipements',
      subtitle: 'Gestion du matériel',
      icon: 'mdi-desktop-classic',
      to: '/equipements',
      permissions: [PERMISSIONS.EQUIPMENT.READ, PERMISSIONS.EQUIPMENT.CREATE]
    },
    {
      title: 'Documents',
      subtitle: 'Gestion documentaire',
      icon: 'mdi-file-document-multiple',
      to: '/documents',
      // Accessible à tous les utilisateurs internes
      userTypes: [USER_TYPES.INTERNAL, USER_TYPES.EXTERNAL],
      permissions: [PERMISSIONS.FILE.UPLOAD, PERMISSIONS.FILE.DOWNLOAD]
    },
    // {
    //   title: 'Templates Documents',
    //   subtitle: 'Modèles de documents',
    //   icon: 'mdi-file-document-edit',
    //   to: '/document-templates',
    //   permissions: [PERMISSIONS.DOCUMENT_TEMPLATE.READ, PERMISSIONS.DOCUMENT_TEMPLATE.CREATE]
    // },
    {
      title: 'Rapports',
      subtitle: 'Analytics et rapports',
      icon: 'mdi-file-chart',
      to: '/reports',
      permissions: [PERMISSIONS.REPORTS.READ, PERMISSIONS.REPORTS.EXPORT]
    },
    {
      title: 'Configuration',
      subtitle: 'Paramètres système',
      icon: 'mdi-cog',
      to: '/configuration',
      userTypes: [USER_TYPES.INTERNAL, USER_TYPES.EXTERNAL],
      permissions: [PERMISSIONS.SETTINGS.READ, PERMISSIONS.SETTINGS.UPDATE]
    },
    {
      title: 'Utilisateurs',
      subtitle: 'Gestion des accès',
      icon: 'mdi-account-cog',
      to: '/utilisateurs',
      userTypes: [USER_TYPES.INTERNAL, USER_TYPES.EXTERNAL],
      permissions: [PERMISSIONS.USER.READ, PERMISSIONS.USER.CREATE, PERMISSIONS.USER.UPDATE]
    }
  ]

  // Menu filtré selon les permissions
  const visibleMenuItems = computed(() => {
    const visible = getVisibleMenuItems(allMenuItems)
    
    // Log du résumé

    const hidden = allMenuItems.filter(item => !isMenuItemVisible(item))
    if (hidden.length > 0) {
      console.log(`🔒 Menus masqués : ${hidden.map(item => item.title).join(', ')}`)
    }
    
    return visible
  })

  // Fonction pour obtenir le résumé des permissions de menu
  const getMenuSummary = () => {
    const visible = getVisibleMenuItems(allMenuItems)
    const hidden = allMenuItems.filter(item => !isMenuItemVisible(item))
    
    return {
      total: allMenuItems.length,
      visible: visible.length,
      hidden: hidden.length,
      visibleItems: visible.map(item => ({
        title: item.title,
        to: item.to,
        reason: 'Autorisé'
      })),
      hiddenItems: hidden.map(item => {
        const user = authStore.user
        let reason = 'Non autorisé'
        
        if (!user) {
          reason = 'Utilisateur non connecté'
        } else {
          // Normaliser le userType pour la comparaison
          const normalizedUserType = user.userType?.toLowerCase() || user.userType
          
          if (item.roles && !item.roles.includes(user.role)) {
            reason = `Rôle "${user.role}" non autorisé (requis: ${item.roles.join(', ')})`
          } else if (item.permissions && !item.permissions.some(p => authStore.hasPermission(p))) {
            const missingPermissions = item.permissions.filter(p => !authStore.hasPermission(p))
            reason = `Permissions manquantes (requises: ${item.permissions.join(', ')}, manquantes: ${missingPermissions.join(', ')})`
          } else if (item.userTypes && item.userTypes.length > 0) {
            const normalizedUserTypes = item.userTypes.map(ut => ut?.toLowerCase() || ut)
            if (!normalizedUserTypes.includes(normalizedUserType)) {
              reason = `Type "${user.userType}" non autorisé (requis: ${item.userTypes.join(', ')})`
            }
          }
        }
        
        return {
          title: item.title,
          to: item.to,
          reason
        }
      })
    }
  }

  return {
    isMenuItemVisible,
    getVisibleMenuItems,
    canAccessRoute,
    allMenuItems,
    visibleMenuItems,
    getMenuSummary
  }
}
